import { Input, Table } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { API } from '@/api'
import { Button, Loading, Modal } from '@/components'
import { debounce } from '@/utils/debounce'

type CharityOrganization = {
  name: string
  donateCode: string
  unifiedCode: string
}

interface CharityModalProps {
  open: boolean
  selectedCharity: { name: string; code: string } | null
  onClose: () => void
  onSelect: (charity: { name: string; code: string }) => void
}

export function CharityModal({ open, selectedCharity, onClose, onSelect }: CharityModalProps) {
  const { t } = useTranslation('billing')
  const [searchText, setSearchText] = useState('')
  const [tempSelected, setTempSelected] = useState<{ name: string; code: string } | null>(
    selectedCharity,
  )
  const [charityOrganizations, setCharityOrganizations] = useState<CharityOrganization[]>([])
  const [loading, setLoading] = useState(false)
  const [, setPagination] = useState({
    page: 1,
    perPage: 100,
    total: 0,
  })

  const fetchOrganizations = useCallback(async (search?: { key?: string }) => {
    setLoading(true)
    try {
      const response = await API.getDonateOrgs({
        page: 1,
        per_page: 100,
        ...search,
      })
      setCharityOrganizations(Array.isArray(response.organizations) ? response.organizations : [])
      setPagination(response.pagination)
    } catch (error) {
      console.error('Failed to load charity organizations:', error)
      setCharityOrganizations([])
    } finally {
      setLoading(false)
    }
  }, [])

  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      const trimmedSearch = searchValue.trim()
      if (trimmedSearch) {
        fetchOrganizations({ key: trimmedSearch })
      } else {
        fetchOrganizations()
      }
    }, 300),
    [fetchOrganizations],
  )

  useEffect(() => {
    if (open) {
      fetchOrganizations()
    }
  }, [open, fetchOrganizations])

  useEffect(() => {
    debouncedSearch(searchText)
    return () => {
      debouncedSearch.cancel()
    }
  }, [searchText, debouncedSearch])

  const handleSelect = () => {
    if (tempSelected) {
      onSelect(tempSelected)
      onClose()
    }
  }

  return (
    <Modal className="w-[640px] h-[640px]" open={open} onClose={onClose}>
      <Modal.Header>
        <Trans ns="billing" i18nKey="charityModal.title">
          Recipient Organization Code
        </Trans>
      </Modal.Header>
      <Modal.Body className="overflow-y-auto scrollbar-hide pb-20">
        <div className="mb-4">
          <Input
            size="large"
            placeholder={t('charityModal.searchPlaceholder')}
            prefix={<span className="text-tertiary">🔍</span>}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loading />
            </div>
          ) : (
            <Table
              dataSource={charityOrganizations}
              rowKey="unifiedCode"
              pagination={false}
              scroll={{ y: 336 }}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: tempSelected
                  ? charityOrganizations
                      .filter((org) => org.donateCode === tempSelected.code)
                      .map((org) => org.unifiedCode)
                  : [],
                onSelect: (record) => {
                  setTempSelected({ name: record.name, code: record.donateCode })
                },
                columnWidth: 50,
              }}
              onRow={(record) => ({
                onClick: () => {
                  setTempSelected({ name: record.name, code: record.donateCode })
                },
                style: { cursor: 'pointer' },
              })}
              locale={{
                emptyText:
                  charityOrganizations.length > 0
                    ? t('charityModal.noMatchingOrgs')
                    : t('charityModal.noData'),
              }}
              columns={[
                {
                  title: t('charityModal.organizationName'),
                  dataIndex: 'name',
                  key: 'name',
                  ellipsis: true,
                },
                {
                  title: t('charityModal.code'),
                  dataIndex: 'donateCode',
                  key: 'donateCode',
                  width: 120,
                },
              ]}
            />
          )}
        </div>

        <div className="flex items-end justify-end gap-4 absolute bottom-0 left-8 right-8 bg-panel-white pb-8">
          <Button type="outline" size="md" onClick={onClose}>
            <Trans ns="billing" i18nKey="charityModal.backButton">
              Back
            </Trans>
          </Button>
          <Button type="secondary" size="md" disabled={!tempSelected} onClick={handleSelect}>
            <Trans ns="billing" i18nKey="charityModal.selectButton">
              Select
            </Trans>
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  )
}
